/**
 * Карта зоны доставки на весь экран.
 *
 * Отдельное окно поверх формы, а не кусок внутри неё: обвести район пальцем
 * на клочке карты в прокручиваемой странице невозможно — страница уезжает
 * под пальцем, а сам район не виден целиком.
 *
 * Форма при этом остаётся смонтированной, поэтому название и цена не теряются,
 * пока исполнитель правит контур.
 */
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import type { CurrentUser, GeoJsonPolygon } from '@arbuz/shared';
import { formatArea, makeT } from '../i18n';
import { haptic, showBackButton } from '../tg';
import { polygonSelfIntersects } from './zoneGeometry';
import { secondaryButton } from './courierUi';
import { errorTextStyle } from './vendorUi';

const PolygonEditor = lazy(async () => {
  await import('@arbuz/ui-map/styles.css');
  const m = await import('@arbuz/ui-map');
  return { default: m.PolygonEditor };
});

interface Props {
  user: CurrentUser;
  /** Контур, с которым карта открывается; null — рисуем с нуля. */
  value: GeoJsonPolygon | null;
  onDone: (geom: GeoJsonPolygon | null) => void;
  onCancel: () => void;
}

export function VendorZoneMap({ user, value, onDone, onCancel }: Props) {
  const t = makeT(user.locale);

  const [draft, setDraft] = useState<GeoJsonPolygon | null>(value);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const labels = useMemo(
    () => ({
      toolDraw: t('zoneMapToolDraw'),
      toolPoints: t('zoneMapToolPoints'),
      noToolHint: t('zoneMapNoToolHint'),
      drawHint: t('zoneMapDrawHint'),
      pointsHint: t('zoneMapPointsHint'),
      pointsCount: (n: number) => `${t('zoneMapPoints')}: ${n}`,
      area: (km2: number) => formatArea(km2, user.locale),
      needMore: t('zoneMapNeedMore'),
      selfIntersects: t('zoneMapCrossing'),
      undo: t('zoneMapUndo'),
      reset: t('zoneMapReset'),
      resetConfirm: t('zoneMapResetConfirm'),
      mapAriaLabel: t('zoneMapAria'),
    }),
    [t, user.locale],
  );

  // Закрытие без сохранения спрашивает подтверждение только если было что терять
  const close = () => {
    if (dirty && !window.confirm(t('zoneMapDiscard'))) return;
    onCancel();
  };

  // Системное «назад» закрывает карту, а не экран под ней: стек обработчиков
  // в `tg.ts` держит подписанным только верхний. Подписка одна на всё время
  // жизни окна — переподписка на каждый рендер копила бы обработчики.
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => showBackButton(() => closeRef.current()), []);

  async function done() {
    if (draft && (await polygonSelfIntersects(draft))) {
      setError(t('zoneMapCrossing'));
      haptic('error');
      return;
    }
    haptic('success');
    onDone(draft);
  }

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label={t('zoneMapTitle')}>
      <header style={headerStyle}>
        <h2 style={{ margin: 0, font: 'var(--fw-semibold) var(--fs-h2)/var(--lh-h2) var(--font)' }}>
          {t('zoneMapTitle')}
        </h2>
        <button type="button" style={cancelStyle} onClick={close}>
          {t('zoneMapCancel')}
        </button>
      </header>

      <div style={{ flex: 1, minHeight: 0 }}>
        <Suspense fallback={<p className="muted" style={{ padding: 'var(--page-x)' }}>{t('mapLoading')}</p>}>
          <PolygonEditor
            value={draft}
            onChange={(geom) => {
              setDraft(geom);
              // Сравниваем с исходным контуром, а не «был вызов onChange»:
              // редактор отдаёт контур сразу при открытии, и иначе «Отмена»
              // всегда спрашивала бы подтверждение, даже если ничего не трогали
              setDirty(JSON.stringify(geom) !== JSON.stringify(value));
              setError(null);
            }}
            labels={labels}
            fill
          />
        </Suspense>
      </div>

      <div style={barStyle}>
        {error && (
          <p role="alert" style={{ ...errorTextStyle, margin: 0 }}>
            {error}
          </p>
        )}
        <button type="button" className="btn-primary" disabled={!draft} onClick={() => void done()}>
          {t('zoneMapDone')}
        </button>
        <button type="button" style={secondaryButton} onClick={close}>
          {t('zoneMapCancel')}
        </button>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  height: '100dvh',
  // Выше фиксированной полосы кнопок формы под окном
  zIndex: 30,
  background: 'var(--c-bg)',
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--s-3)',
  padding: 'var(--s-3) var(--page-x)',
  paddingTop: 'calc(var(--s-3) + var(--tg-safe-top))',
  borderBottom: '1px solid var(--c-border)',
};

const cancelStyle: React.CSSProperties = {
  minHeight: 'var(--tap-min)',
  padding: '0 var(--s-2)',
  background: 'none',
  border: 'none',
  color: 'var(--c-primary-text)',
  font: 'var(--fw-medium) var(--fs-body)/var(--lh-body) var(--font)',
  cursor: 'pointer',
};

/**
 * Полоса действий внизу окна. `--tg-safe-bottom` обязателен: на телефонах с
 * жестовой навигацией «Готово» иначе оказывается под системной полосой.
 */
const barStyle: React.CSSProperties = {
  padding: 'var(--s-3) var(--page-x)',
  paddingBottom: 'calc(var(--s-3) + var(--tg-safe-bottom))',
  background: 'var(--c-surface)',
  borderTop: '1px solid var(--c-border)',
  display: 'grid',
  gap: 'var(--s-2)',
};
